import {
  HeroSlide,
  ResearchSlide,
  ObservationSlide,
  ComparisonSlide,
  ProductWalkthroughSlide,
  MetricsDashboardSlide,
  TimelineSlide,
  QuoteSlide,
  BusinessModelSlide,
  FounderSlide,
  ClosingSlide,
} from "@/components/presentation/slides";

/**
 * Phase 6 · Narrative Chapter Engine
 *
 * A single dispatcher that turns a chapter object into a rendered slide.
 * The chapter schema is intentionally a superset of every individual slide's
 * props, and each layout's mapper picks the fields it needs.
 *
 * Adding a new layout means:
 *   1. write a slide component (already done for the 11 in /slides/)
 *   2. add it to LAYOUT_REGISTRY here
 *   3. add a prop mapper if the schema → slide-props translation is non-trivial
 *
 * Chapters never know about slides directly — they describe themselves and the
 * engine routes them. That is the whole point of the engine: future content
 * iterations edit data in `lib/sections.js`, never markup.
 *
 * ── Canonical chapter schema ────────────────────────────────────────────
 * {
 *   id,                    // unique
 *   label,                 // "Chapter 02"
 *   title,                 // "HAEVN Labs" (chapter name)
 *   headline,              // primary headline shown by the slide
 *   narrative,             // supporting paragraph
 *   layout,                // one of LAYOUT_REGISTRY keys
 *   primaryVisual,         // shape varies by layout
 *   secondaryVisual,       // optional, comparison/observation
 *   metrics,               // optional, for dashboard
 *   quote,                 // optional
 *   callout,               // optional { title, body, variant }
 *   evidence,              // optional { source }
 *   continueLabel,         // optional override for the closing slide CTA
 *   custom,                // bypass the engine (Opening uses "opening")
 * }
 */

const LAYOUT_REGISTRY = {
  hero:             HeroSlide,
  research:         ResearchSlide,
  observation:      ObservationSlide,
  comparison:       ComparisonSlide,
  product:          ProductWalkthroughSlide,
  dashboard:        MetricsDashboardSlide,
  timeline:         TimelineSlide,
  quote:            QuoteSlide,
  "business-model": BusinessModelSlide,
  founder:          FounderSlide,
  closing:          ClosingSlide,
};

/**
 * Maps the unified chapter schema onto each slide template's prop shape.
 * Every mapper accepts (chapter, ctx) where ctx exposes runtime helpers
 * (e.g. onContinue, onEnterExplore) that the engine itself controls.
 */
const PROP_MAPPERS = {
  hero: (c, ctx) => ({
    eyebrow: c.label,
    title: c.headline,
    lede: c.narrative,
    visualLabel: c.primaryVisual?.label ?? "Hero visual",
    continueLabel: c.continueLabel ?? "Continue",
    onContinue: ctx.onContinue,
  }),
  research: (c) => ({
    eyebrow: c.label,
    title: c.headline,
    narrative: c.narrative,
    chartVariant: c.primaryVisual?.variant ?? "line",
    calloutTitle: c.callout?.title,
    calloutBody: c.callout?.body,
    source: c.evidence?.source,
  }),
  observation: (c) => ({
    eyebrow: c.label,
    title: c.headline,
    narrative: c.narrative,
    beforeLabel: c.primaryVisual?.label,
    afterLabel: c.secondaryVisual?.label,
    insightTitle: c.callout?.title,
    insightBody: c.callout?.body,
  }),
  comparison: (c) => ({
    eyebrow: c.label,
    title: c.headline,
    mode: c.primaryVisual?.mode ?? "before-after",
    left: {
      label: c.primaryVisual?.label,
      body: c.primaryVisual?.body,
      visualLabel: c.primaryVisual?.visualLabel,
    },
    right: {
      label: c.secondaryVisual?.label,
      body: c.secondaryVisual?.body,
      visualLabel: c.secondaryVisual?.visualLabel,
    },
  }),
  product: (c) => ({
    eyebrow: c.label,
    title: c.headline,
    narrative: c.narrative,
    screens: c.primaryVisual?.screens,
  }),
  dashboard: (c) => ({
    eyebrow: c.label,
    title: c.headline,
    kpis: c.metrics,
    chartVariant: c.primaryVisual?.variant ?? "line",
    insightTitle: c.callout?.title,
    insightBody: c.callout?.body,
  }),
  timeline: (c) => ({
    eyebrow: c.label,
    title: c.headline,
    narrative: c.narrative,
    milestones: c.primaryVisual?.milestones,
    progressLabel: c.primaryVisual?.progressLabel,
  }),
  quote: (c) => ({
    eyebrow: c.label,
    quote: c.quote?.value,
    attribution: c.quote?.attribution,
    role: c.quote?.role,
    commentary: c.quote?.commentary,
  }),
  "business-model": (c) => ({
    eyebrow: c.label,
    title: c.headline,
    narrative: c.narrative,
    flow: c.primaryVisual?.flow,
    pricing: c.primaryVisual?.pricing,
    unitEconomics: c.primaryVisual?.unitEconomics,
    insight: c.callout?.body,
  }),
  founder: (c) => ({
    eyebrow: c.label,
    title: c.headline,
    narrative: c.narrative,
    portraitLabel: c.primaryVisual?.label,
    milestones: c.primaryVisual?.milestones,
  }),
  closing: (c, ctx) => ({
    eyebrow: c.label,
    title: c.headline,
    narrative: c.narrative,
    ctaLabel: c.continueLabel ?? "Get in touch",
    onCta: ctx.onContinue,
    onEnterExplore: ctx.onEnterExplore,
  }),
};

const stripUndefined = (obj) => {
  const out = {};
  Object.keys(obj).forEach((k) => {
    if (obj[k] !== undefined) out[k] = obj[k];
  });
  return out;
};

/**
 * Render a chapter via the engine.
 *
 * `ctx` is injected by PresentationView so any chapter can hook into the
 * shared chrome (Continue button, hand-off to Explore Mode) without needing
 * direct access to navigation state.
 */
export const NarrativeChapter = ({ chapter, ctx = {} }) => {
  if (!chapter) return null;
  if (!chapter.layout) {
    if (typeof console !== "undefined" && process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.warn(
        `[NarrativeChapter] chapter "${chapter.id}" has no layout; nothing will render.`
      );
    }
    return null;
  }

  const Slide = LAYOUT_REGISTRY[chapter.layout];
  if (!Slide) {
    // Unknown layout — degrade gracefully into Hero with the chapter's
    // headline / narrative so the presentation never breaks.
    return (
      <HeroSlide
        eyebrow={chapter.label}
        title={chapter.headline ?? chapter.title}
        lede={chapter.narrative}
        onContinue={ctx.onContinue}
      />
    );
  }

  const map = PROP_MAPPERS[chapter.layout] ?? PROP_MAPPERS.hero;
  const props = stripUndefined(map(chapter, ctx));
  return <Slide {...props} />;
};

export default NarrativeChapter;
